import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { requestReset, resetPassword } from "@/api/auth";
import { ApiException } from "@/api/client";

export function ResetPage() {
	const [email, setEmail] = useState("");
	const [code, setCode] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [step, setStep] = useState<"request" | "reset">("request");
	const [message, setMessage] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	async function onRequest(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setMessage(null);
		setBusy(true);
		try {
			await requestReset(email);
			setMessage(
				"If that email is registered, a reset code was sent. Check your inbox.",
			);
			setStep("reset");
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Request failed",
			);
		} finally {
			setBusy(false);
		}
	}

	async function onReset(e: FormEvent) {
		e.preventDefault();
		setError(null);
		setMessage(null);
		setBusy(true);
		try {
			await resetPassword(code, newPassword);
			setMessage("Password updated. You can log in now.");
		} catch (err) {
			setError(
				err instanceof ApiException ? err.message : "Reset failed",
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<div className="grid min-h-svh place-items-center px-4">
			<div className="w-full max-w-md space-y-6">
				<div className="text-center">
					<h1 className="text-2xl font-semibold tracking-tight">
						Reset password
					</h1>
				</div>

				{message && (
					<div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300">
						{message}
					</div>
				)}
				{error && (
					<div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
						{error}
					</div>
				)}

				{step === "request" ? (
					<form
						onSubmit={onRequest}
						className="space-y-3 rounded-xl border border-border bg-surface-raised p-5"
					>
						<label className="block text-sm">
							<span className="text-fg-muted">Admin email</span>
							<input
								type="email"
								required
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40"
							/>
						</label>
						<button
							type="submit"
							disabled={busy}
							className="w-full rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
						>
							Send code
						</button>
					</form>
				) : (
					<form
						onSubmit={onReset}
						className="space-y-3 rounded-xl border border-border bg-surface-raised p-5"
					>
						<label className="block text-sm">
							<span className="text-fg-muted">Code</span>
							<input
								type="text"
								required
								value={code}
								onChange={(e) => setCode(e.target.value)}
								className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 font-mono uppercase tracking-widest outline-none focus:ring-2 focus:ring-accent/40"
							/>
						</label>
						<label className="block text-sm">
							<span className="text-fg-muted">New password</span>
							<input
								type="password"
								required
								minLength={8}
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								className="mt-1 w-full rounded-lg border border-border bg-surface px-3 py-2 outline-none focus:ring-2 focus:ring-accent/40"
							/>
						</label>
						<button
							type="submit"
							disabled={busy}
							className="w-full rounded-lg bg-teal-500 px-3 py-2 text-sm font-medium text-white hover:bg-teal-600 disabled:opacity-50"
						>
							Set password
						</button>
					</form>
				)}

				<p className="text-center text-sm text-fg-muted">
					<Link to="/login" className="text-accent hover:underline">
						Back to login
					</Link>
				</p>
			</div>
		</div>
	);
}
